/**
 * Team kit order builder
 *
 * Cart integration follows Dawn product-form.js:
 * - POST to routes.cart_add_url (cart/add.js) with items + sections
 * - publish(PUB_SUB_EVENTS.cartUpdate)
 * - cart-notification || cart-drawer → renderContents()
 *
 * Do NOT redirect to /cart (bulk-variant-matrix pattern).
 */
if (!customElements.get('team-kit-order-builder')) {
  customElements.define(
    'team-kit-order-builder',
    class TeamKitOrderBuilder extends HTMLElement {
      constructor() {
        super();
        this.submitting = false;
        this.rowIndex = 0;
        this.products = {};
        this.productList = [];
        this.openProductPickerRow = null;
        this.sponsorService = null;
        this.sponsorServiceError = null;
        this.initialsService = null;
        this.initialsServiceError = null;
        this.numberService = null;
        this.numberServiceError = null;
        this.builderVersion = '1.3';
        this.moneyFormat = '{{amount}}';
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('[data-team-kit-form]');
        if (!this.form) return;

        this.rowsContainer = this.querySelector('[data-team-kit-rows]');
        this.rowTemplate = this.querySelector('[data-team-kit-row-template]');
        this.statusEl = this.querySelector('[data-team-kit-status]');
        this.totalEl = this.querySelector('[data-team-kit-total]');
        this.submitButton = this.querySelector('[data-team-kit-submit]');
        this.addRowButton = this.querySelector('[data-team-kit-add-row]');
        this.spinner = this.submitButton?.querySelector('.loading__spinner');

        // Dawn cart UI — same resolution order as product-form.js
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.loadProductData();
        this.bindSharedEvents();

        if (this.rowsContainer && !this.rowsContainer.children.length) {
          this.addRow();
        } else {
          this.rowsContainer?.querySelectorAll('[data-team-kit-row]').forEach((row) => this.bindRow(row));
          this.updateTotal();
        }
      }

      loadProductData() {
        const dataEl = this.querySelector('[data-team-kit-products]');
        if (!dataEl) return;

        try {
          const payload = JSON.parse(dataEl.textContent.trim());
          this.moneyFormat = payload.moneyFormat || this.moneyFormat;
          this.builderVersion = payload.builderVersion || this.builderVersion;
          this.products = this.normalizeProductCatalogue(payload.products);
          this.productList = this.buildProductList(payload.products);
          this.sponsorServiceError = payload.sponsorServiceError || null;
          this.sponsorService =
            payload.sponsorService && payload.sponsorService.variantId
              ? payload.sponsorService
              : null;

          if (this.sponsorServiceError === 'multiple') {
            console.error(
              '[team-kit-order-builder] Configuration error: more than one available variant with SKU TEAMKIT-SPONSOR was found in this collection. Sponsor service is unavailable until exactly one match remains.'
            );
            this.sponsorService = null;
          }

          this.initialsServiceError = payload.initialsServiceError || null;
          this.initialsService =
            payload.initialsService && payload.initialsService.variantId
              ? payload.initialsService
              : null;

          if (this.initialsServiceError === 'multiple') {
            console.error(
              '[team-kit-order-builder] Configuration error: more than one available variant with SKU TEAMKIT-INITIALS was found in this collection. Initials service is unavailable until exactly one match remains.'
            );
            this.initialsService = null;
          }

          this.numberServiceError = payload.numberServiceError || null;
          this.numberService =
            payload.numberService && payload.numberService.variantId
              ? payload.numberService
              : null;

          if (this.numberServiceError === 'multiple') {
            console.error(
              '[team-kit-order-builder] Configuration error: more than one available variant with SKU TEAMKIT-NUMBER was found in this collection. Number service is unavailable until exactly one match remains.'
            );
            this.numberService = null;
          }
        } catch (error) {
          console.error('[team-kit-order-builder] Failed to parse product data', error);
          this.products = {};
          this.productList = [];
          this.sponsorService = null;
          this.sponsorServiceError = null;
          this.initialsService = null;
          this.initialsServiceError = null;
          this.numberService = null;
          this.numberServiceError = null;
        }
      }

      buildProductList(rawProducts) {
        if (Array.isArray(rawProducts)) {
          return rawProducts
            .map((product) => (product?.id != null ? this.products[String(product.id)] : null))
            .filter(Boolean);
        }
        return Object.keys(this.products).map((id) => this.products[id]);
      }

      /**
       * Accept either:
       * - products: [{ id, title, variants }]  (preferred valid JSON from Liquid)
       * - products: { "123": { id, title, variants } } (legacy object map)
       * Always index by string ID for reliable select-value lookups.
       */
      normalizeProductCatalogue(rawProducts) {
        const map = {};

        if (Array.isArray(rawProducts)) {
          rawProducts.forEach((product) => {
            if (!product || product.id == null) return;
            map[String(product.id)] = this.normalizeProductRecord(product);
          });
          return map;
        }

        if (rawProducts && typeof rawProducts === 'object') {
          Object.keys(rawProducts).forEach((key) => {
            const product = rawProducts[key];
            if (!product) return;
            const id = product.id != null ? product.id : key;
            map[String(id)] = this.normalizeProductRecord({ ...product, id });
          });
        }

        return map;
      }

      normalizeProductRecord(product) {
        const features = product?.features && typeof product.features === 'object' ? product.features : {};
        return {
          ...product,
          id: product.id,
          imageUrl: typeof product.imageUrl === 'string' && product.imageUrl ? product.imageUrl : null,
          features: {
            number: Boolean(features.number),
            initials: Boolean(features.initials),
            sponsor: Boolean(features.sponsor),
          },
          variants: Array.isArray(product.variants) ? product.variants : [],
        };
      }

      getProduct(productId) {
        if (productId == null || productId === '') return null;
        return this.products[String(productId)] || null;
      }

      getProductFeatures(productId) {
        const product = this.getProduct(productId);
        return {
          number: Boolean(product?.features?.number),
          initials: Boolean(product?.features?.initials),
          sponsor: Boolean(product?.features?.sponsor),
        };
      }

      getSponsorQuantity(rows = this.collectRows()) {
        return rows.reduce((sum, row) => {
          if (!row.newSponsor || !row.allowSponsor) return sum;
          if (!Number.isFinite(row.quantity) || row.quantity < 1) return sum;
          return sum + row.quantity;
        }, 0);
      }

      getInitialsQuantity(rows = this.collectRows()) {
        return rows.reduce((sum, row) => {
          if (!row.initials || !row.allowInitials) return sum;
          if (!Number.isFinite(row.quantity) || row.quantity < 1) return sum;
          return sum + row.quantity;
        }, 0);
      }

      getNumberQuantity(rows = this.collectRows()) {
        return rows.reduce((sum, row) => {
          if (!row.playerNumber || !row.allowNumber) return sum;
          if (!Number.isFinite(row.quantity) || row.quantity < 1) return sum;
          return sum + row.quantity;
        }, 0);
      }

      setTextCapability(input, enabled, preserveValue = '', { uppercase = false } = {}) {
        if (!input) return;

        if (enabled) {
          input.disabled = false;
          input.removeAttribute('disabled');
          input.setAttribute('aria-disabled', 'false');
          input.placeholder = '';
          let next = String(preserveValue || '');
          if (uppercase) next = next.toUpperCase().slice(0, Number(input.maxLength) || 4);
          input.value = next;
        } else {
          input.value = '';
          input.disabled = true;
          input.setAttribute('disabled', 'disabled');
          input.setAttribute('aria-disabled', 'true');
          input.placeholder = 'N/A';
          this.clearFieldError(input);
        }
      }

      /**
       * Enable/disable Number, Initials and Sponsor controls from product.features.
       * Clears values when a capability is no longer supported.
       */
      syncFeatureFields(row, productId, preserve = {}) {
        const features = this.getProductFeatures(productId);
        const numberInput = row.querySelector('[data-number-input]');
        const initialsInput = row.querySelector('[data-initials-input]');
        const sponsorInput = row.querySelector('[data-sponsor-input]');
        const sponsorField = row.querySelector('.team-kit-order-builder__field--sponsor');
        const sponsorNa = row.querySelector('[data-sponsor-na]');

        this.setTextCapability(numberInput, features.number, preserve.number || '');
        this.setTextCapability(initialsInput, features.initials, preserve.initials || '', { uppercase: true });

        if (sponsorInput) {
          if (features.sponsor) {
            sponsorInput.disabled = false;
            sponsorInput.removeAttribute('disabled');
            sponsorInput.setAttribute('aria-disabled', 'false');
            sponsorInput.checked = Boolean(preserve.sponsor);
            sponsorField?.classList.remove('is-na');
            if (sponsorNa) sponsorNa.setAttribute('aria-hidden', 'true');
          } else {
            sponsorInput.checked = false;
            sponsorInput.disabled = true;
            sponsorInput.setAttribute('disabled', 'disabled');
            sponsorInput.setAttribute('aria-disabled', 'true');
            sponsorField?.classList.add('is-na');
            if (sponsorNa) sponsorNa.setAttribute('aria-hidden', 'false');
          }
        }
      }

      bindSharedEvents() {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.addRowButton?.addEventListener('click', () => this.addRow());

        this.form.addEventListener('input', () => {
          this.clearStatus();
          this.updateTotal();
        });
        this.form.addEventListener('change', () => {
          this.clearStatus();
          this.updateTotal();
        });

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      handleDocumentClick(event) {
        if (!this.openProductPickerRow) return;
        const picker = this.openProductPickerRow.querySelector('[data-product-picker]');
        if (picker && !picker.contains(event.target)) {
          this.closeProductPicker(this.openProductPickerRow);
        }
      }

      handleDocumentKeydown(event) {
        if (event.key === 'Escape' && this.openProductPickerRow) {
          const row = this.openProductPickerRow;
          this.closeProductPicker(row);
          row.querySelector('[data-product-picker-trigger]')?.focus();
        }
      }

      createProductThumb(imageUrl, title) {
        if (imageUrl) {
          const img = document.createElement('img');
          img.src = imageUrl;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 40;
          img.height = 40;
          if (title) img.setAttribute('data-title', title);
          return img;
        }

        const placeholder = document.createElement('span');
        placeholder.className = 'team-kit-order-builder__product-thumb-empty';
        placeholder.setAttribute('aria-hidden', 'true');
        return placeholder;
      }

      syncProductPickerDisplay(row) {
        const select = row.querySelector('[data-product-select]');
        const trigger = row.querySelector('[data-product-picker-trigger]');
        const thumb = row.querySelector('[data-product-picker-thumb]');
        const label = row.querySelector('[data-product-picker-label]');
        if (!select || !trigger || !thumb || !label) return;

        const productId = select.value || '';
        const product = productId ? this.getProduct(productId) : null;
        const title = product?.title || 'Select product';

        thumb.replaceChildren(this.createProductThumb(product?.imageUrl || null, title));
        label.textContent = title;
        trigger.setAttribute('aria-label', product ? `Product: ${title}` : 'Select product');

        const list = row.querySelector('[data-product-picker-list]');
        list?.querySelectorAll('[data-product-option]').forEach((option) => {
          const selected = option.getAttribute('data-product-id') === productId;
          option.setAttribute('aria-selected', selected ? 'true' : 'false');
          option.classList.toggle('is-selected', selected);
        });
      }

      renderProductPickerOptions(row) {
        const list = row.querySelector('[data-product-picker-list]');
        if (!list) return;

        list.replaceChildren();

        this.productList.forEach((product) => {
          const option = document.createElement('button');
          option.type = 'button';
          option.className = 'team-kit-order-builder__product-option';
          option.setAttribute('role', 'option');
          option.setAttribute('data-product-option', '');
          option.setAttribute('data-product-id', String(product.id));
          option.setAttribute('aria-selected', 'false');

          const thumb = document.createElement('span');
          thumb.className = 'team-kit-order-builder__product-thumb';
          thumb.setAttribute('aria-hidden', 'true');
          thumb.appendChild(this.createProductThumb(product.imageUrl, product.title));

          const title = document.createElement('span');
          title.className = 'team-kit-order-builder__product-option-title';
          title.textContent = product.title || `Product ${product.id}`;

          option.appendChild(thumb);
          option.appendChild(title);
          list.appendChild(option);
        });
      }

      setProductPickerOverflow(enabled) {
        this.querySelector('.team-kit-order-builder__rows-wrap')?.classList.toggle('has-open-picker', enabled);
        this.querySelector('.team-kit-order-builder__rows')?.classList.toggle('has-open-picker', enabled);
      }

      openProductPicker(row) {
        if (this.openProductPickerRow && this.openProductPickerRow !== row) {
          this.closeProductPicker(this.openProductPickerRow);
        }

        const trigger = row.querySelector('[data-product-picker-trigger]');
        const list = row.querySelector('[data-product-picker-list]');
        if (!trigger || !list) return;

        list.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        row.querySelector('[data-product-picker]')?.classList.add('is-open');
        this.openProductPickerRow = row;
        this.setProductPickerOverflow(true);

        const selected = list.querySelector('[aria-selected="true"]') || list.querySelector('[data-product-option]');
        selected?.focus();
      }

      closeProductPicker(row) {
        if (!row) return;
        const trigger = row.querySelector('[data-product-picker-trigger]');
        const list = row.querySelector('[data-product-picker-list]');
        if (list) list.hidden = true;
        trigger?.setAttribute('aria-expanded', 'false');
        row.querySelector('[data-product-picker]')?.classList.remove('is-open');
        if (this.openProductPickerRow === row) this.openProductPickerRow = null;
        if (!this.openProductPickerRow) this.setProductPickerOverflow(false);
      }

      toggleProductPicker(row) {
        if (this.openProductPickerRow === row) {
          this.closeProductPicker(row);
          return;
        }
        this.openProductPicker(row);
      }

      selectProductFromPicker(row, productId) {
        const select = row.querySelector('[data-product-select]');
        if (!select) return;

        const nextValue = productId == null ? '' : String(productId);
        if (select.value !== nextValue) {
          select.value = nextValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.syncProductPickerDisplay(row);
        }

        this.closeProductPicker(row);
        row.querySelector('[data-product-picker-trigger]')?.focus();
      }

      bindProductPicker(row) {
        const picker = row.querySelector('[data-product-picker]');
        const trigger = row.querySelector('[data-product-picker-trigger]');
        const list = row.querySelector('[data-product-picker-list]');
        const select = row.querySelector('[data-product-select]');
        if (!picker || !trigger || !list || !select || picker.dataset.pickerBound === 'true') return;

        picker.dataset.pickerBound = 'true';
        this.renderProductPickerOptions(row);
        this.syncProductPickerDisplay(row);

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggleProductPicker(row);
        });

        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (this.openProductPickerRow !== row) this.openProductPicker(row);
          }
        });

        list.addEventListener('click', (event) => {
          const option = event.target.closest('[data-product-option]');
          if (!option || !list.contains(option)) return;
          event.preventDefault();
          this.selectProductFromPicker(row, option.getAttribute('data-product-id'));
        });

        list.addEventListener('keydown', (event) => {
          const options = Array.from(list.querySelectorAll('[data-product-option]'));
          if (!options.length) return;

          const currentIndex = options.indexOf(document.activeElement);
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            const next = options[Math.min(options.length - 1, Math.max(0, currentIndex) + 1)];
            next?.focus();
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prev = options[Math.max(0, currentIndex - 1)];
            (currentIndex <= 0 ? trigger : prev)?.focus();
          } else if (event.key === 'Home') {
            event.preventDefault();
            options[0]?.focus();
          } else if (event.key === 'End') {
            event.preventDefault();
            options[options.length - 1]?.focus();
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const active = document.activeElement?.closest?.('[data-product-option]');
            if (active) this.selectProductFromPicker(row, active.getAttribute('data-product-id'));
          } else if (event.key === 'Tab') {
            this.closeProductPicker(row);
          }
        });
      }

      addRow(sourceRow) {
        if (!this.rowTemplate || !this.rowsContainer) return;

        const fragment = this.rowTemplate.content.cloneNode(true);
        const row = fragment.querySelector('[data-team-kit-row]');
        const id = `row-${Date.now()}-${this.rowIndex++}`;
        row.dataset.rowId = id;

        row.querySelectorAll('[data-field-id]').forEach((el) => {
          const key = el.getAttribute('data-field-id');
          const fieldId = `TeamKit-${key}-${id}`;
          el.id = fieldId;
        });

        row.querySelectorAll('[data-field-error]').forEach((errorEl) => {
          const field = errorEl.closest('[data-field]');
          const control = field?.querySelector('[data-field-id]');
          const key = control?.getAttribute('data-field-id') || 'field';
          errorEl.id = `TeamKit-${key}-error-${id}`;
        });

        row.querySelectorAll('[data-label-for]').forEach((label) => {
          const key = label.getAttribute('data-label-for');
          label.setAttribute('for', `TeamKit-${key}-${id}`);
        });

        const productTrigger = row.querySelector('[data-product-picker-trigger]');
        const productList = row.querySelector('[data-product-picker-list]');
        const productLabel = row.querySelector('[data-label-for="product"]');
        if (productTrigger) {
          productTrigger.id = `TeamKit-product-trigger-${id}`;
          if (productLabel) productLabel.setAttribute('for', productTrigger.id);
        }
        if (productList) {
          productList.id = `TeamKit-product-list-${id}`;
          productTrigger?.setAttribute('aria-controls', productList.id);
        }

        const sourceProductId = sourceRow?.querySelector('[data-product-select]')?.value || '';
        const sourceVariantId = sourceRow?.querySelector('[data-variant-select]')?.value || '';
        const sourceNumber = sourceRow?.querySelector('[data-number-input]')?.value || '';
        const sourceInitials = sourceRow?.querySelector('[data-initials-input]')?.value || '';
        const sourceSponsor = Boolean(sourceRow?.querySelector('[data-sponsor-input]')?.checked);

        this.rowsContainer.appendChild(row);
        this.bindRow(row);

        if (sourceRow) {
          this.copyRowValues(sourceRow, row, { skipVariant: true });
          this.populateVariants(row, sourceProductId, sourceVariantId);
          this.syncFeatureFields(row, sourceProductId, {
            number: sourceNumber,
            initials: sourceInitials,
            sponsor: sourceSponsor,
          });
          this.syncProductPickerDisplay(row);
        } else {
          this.populateVariants(row, '');
          this.syncFeatureFields(row, '');
          this.syncProductPickerDisplay(row);
        }

        this.updateRemoveButtons();
        this.updateTotal();
        row.querySelector('[data-product-picker-trigger]')?.focus();
      }

      copyRowValues(sourceRow, targetRow, options = {}) {
        const fields = options.skipVariant
          ? ['product', 'quantity']
          : ['product', 'variant', 'quantity'];

        fields.forEach((name) => {
          const source = sourceRow.querySelector(`[name="${name}"]`);
          const target = targetRow.querySelector(`[name="${name}"]`);
          if (source && target) target.value = source.value;
        });
      }

      bindRow(row) {
        if (row.dataset.bound === 'true') return;
        row.dataset.bound = 'true';

        const productSelect = row.querySelector('[data-product-select]');
        const variantSelect = row.querySelector('[data-variant-select]');
        const numberInput = row.querySelector('[data-number-input]');
        const initialsInput = row.querySelector('[data-initials-input]');
        const duplicateBtn = row.querySelector('[data-duplicate-row]');
        const removeBtn = row.querySelector('[data-remove-row]');

        this.bindProductPicker(row);

        productSelect?.addEventListener('change', () => {
          // Reset size + clear unsupported feature values for the new product.
          this.populateVariants(row, productSelect.value, '');
          this.syncFeatureFields(row, productSelect.value, {});
          this.syncProductPickerDisplay(row);
          this.clearFieldError(productSelect);
          this.clearFieldError(variantSelect);
          this.clearFieldError(numberInput);
          this.clearFieldError(initialsInput);
          this.updateTotal();
        });

        variantSelect?.addEventListener('change', () => {
          this.clearFieldError(variantSelect);
          this.updateTotal();
        });

        numberInput?.addEventListener('input', () => {
          this.clearFieldError(numberInput);
          this.updateTotal();
        });

        initialsInput?.addEventListener('input', () => {
          const caret = initialsInput.selectionStart;
          initialsInput.value = initialsInput.value.toUpperCase().slice(0, 4);
          if (typeof caret === 'number') {
            initialsInput.setSelectionRange(caret, caret);
          }
          this.clearFieldError(initialsInput);
          this.updateTotal();
        });

        duplicateBtn?.addEventListener('click', () => this.addRow(row));
        removeBtn?.addEventListener('click', () => this.removeRow(row));

        if (productSelect?.value) {
          this.populateVariants(row, productSelect.value, variantSelect?.value || '');
          this.syncFeatureFields(row, productSelect.value, {
            number: numberInput?.value || '',
            initials: initialsInput?.value || '',
            sponsor: Boolean(row.querySelector('[data-sponsor-input]')?.checked),
          });
        } else {
          this.populateVariants(row, '');
          this.syncFeatureFields(row, '');
        }

        this.syncProductPickerDisplay(row);
      }

      removeRow(row) {
        if (this.rowsContainer.querySelectorAll('[data-team-kit-row]').length <= 1) {
          this.showStatus('Keep at least one garment row, or clear the fields instead.', 'error');
          return;
        }

        if (this.openProductPickerRow === row) this.closeProductPicker(row);
        row.remove();
        this.updateRemoveButtons();
        this.updateTotal();
      }

      updateRemoveButtons() {
        const rows = this.rowsContainer.querySelectorAll('[data-team-kit-row]');
        rows.forEach((row) => {
          const removeBtn = row.querySelector('[data-remove-row]');
          if (removeBtn) removeBtn.disabled = rows.length <= 1;
        });
      }

      populateVariants(row, productId, selectedVariantId = '') {
        const variantSelect = row.querySelector('[data-variant-select]');
        if (!variantSelect) return;

        const placeholderLabel = 'Select a size';
        variantSelect.innerHTML = '';

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = placeholderLabel;
        variantSelect.appendChild(placeholder);

        const normalizedProductId = productId == null ? '' : String(productId);

        if (!normalizedProductId) {
          variantSelect.value = '';
          variantSelect.disabled = true;
          variantSelect.setAttribute('disabled', 'disabled');
          return;
        }

        const product = this.getProduct(normalizedProductId);
        if (!product) {
          console.warn('[team-kit-order-builder] Product not found in catalogue:', normalizedProductId);
          variantSelect.value = '';
          variantSelect.disabled = true;
          variantSelect.setAttribute('disabled', 'disabled');
          return;
        }

        const variants = Array.isArray(product.variants) ? product.variants : [];
        const availableVariants = variants.filter((variant) => {
          if (!variant || variant.id == null) return false;
          // Treat missing `available` as true; Liquid currently only emits available variants.
          return variant.available !== false;
        });

        availableVariants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = variant.title || `Variant ${variant.id}`;
          option.dataset.price = String(variant.price ?? 0);
          variantSelect.appendChild(option);
        });

        if (availableVariants.length === 0) {
          variantSelect.value = '';
          variantSelect.disabled = true;
          variantSelect.setAttribute('disabled', 'disabled');
          return;
        }

        // Enable before setting value so the control is interactive.
        variantSelect.disabled = false;
        variantSelect.removeAttribute('disabled');

        const preferredId = selectedVariantId != null && selectedVariantId !== ''
          ? String(selectedVariantId)
          : '';

        if (preferredId && availableVariants.some((variant) => String(variant.id) === preferredId)) {
          variantSelect.value = preferredId;
        } else {
          // Keep placeholder selected so the shopper explicitly chooses a size,
          // including when only one variant exists.
          variantSelect.value = '';
          variantSelect.selectedIndex = 0;
        }
      }

      getSharedValues() {
        return {
          team: this.form.querySelector('[name="team-squad"]')?.value.trim() || '',
          manager: this.form.querySelector('[name="manager-contact"]')?.value.trim() || '',
          sponsorName: this.form.querySelector('[name="sponsor-name"]')?.value.trim() || '',
          instructions: this.form.querySelector('[name="instructions"]')?.value.trim() || '',
        };
      }

      collectRows() {
        return Array.from(this.rowsContainer.querySelectorAll('[data-team-kit-row]')).map((row) => {
          const productSelect = row.querySelector('[data-product-select]');
          const variantSelect = row.querySelector('[data-variant-select]');
          const productId = productSelect?.value || '';
          const variantId = variantSelect?.value || '';
          const product = this.getProduct(productId);
          const variant = product?.variants?.find((item) => String(item.id) === String(variantId));

          return {
            row,
            productSelect,
            variantSelect,
            productId,
            variantId,
            productTitle: product?.title || '',
            sizeTitle: variant?.title || '',
            price: variant ? Number(variant.price) : 0,
            playerNumber: row.querySelector('[data-number-input]')?.value.trim() || '',
            initials: (row.querySelector('[data-initials-input]')?.value || '').trim().toUpperCase(),
            allowNumber: Boolean(product?.features?.number),
            allowInitials: Boolean(product?.features?.initials),
            allowSponsor: Boolean(product?.features?.sponsor),
            quantity: Number(row.querySelector('[name="quantity"]')?.value || 0),
            newSponsor: Boolean(row.querySelector('[data-sponsor-input]')?.checked),
            quantityInput: row.querySelector('[name="quantity"]'),
            playerInput: row.querySelector('[data-number-input]'),
            initialsInput: row.querySelector('[data-initials-input]'),
            sponsorInput: row.querySelector('[data-sponsor-input]'),
          };
        });
      }

      validate() {
        let valid = true;
        this.clearAllFieldErrors();

        const shared = this.getSharedValues();
        const teamInput = this.form.querySelector('[name="team-squad"]');
        const managerInput = this.form.querySelector('[name="manager-contact"]');

        if (!shared.team) {
          this.setFieldError(teamInput, 'Enter a team or squad name.');
          valid = false;
        }

        if (!shared.manager) {
          this.setFieldError(managerInput, 'Enter a manager or contact name.');
          valid = false;
        }

        const rows = this.collectRows();
        if (!rows.length) {
          this.showStatus('Add at least one garment row.', 'error');
          return false;
        }

        rows.forEach((rowData, index) => {
          if (!rowData.productId) {
            this.setFieldError(rowData.productSelect, 'Select a garment.');
            valid = false;
          }

          if (!rowData.variantId) {
            this.setFieldError(rowData.variantSelect, 'Select a size.');
            valid = false;
          }

          if (!Number.isFinite(rowData.quantity) || rowData.quantity < 1) {
            this.setFieldError(rowData.quantityInput, 'Enter a quantity of at least 1.');
            valid = false;
          }

          if (rowData.quantity > 999) {
            this.setFieldError(rowData.quantityInput, 'Quantity cannot exceed 999.');
            valid = false;
          }

          if (rowData.playerNumber && !rowData.allowNumber) {
            this.setFieldError(rowData.playerInput, 'Player number is not available for this product.');
            valid = false;
          }

          if (rowData.initials && !rowData.allowInitials) {
            this.setFieldError(rowData.initialsInput, 'Initials are not available for this product.');
            valid = false;
          }

          if (rowData.newSponsor && !rowData.allowSponsor) {
            this.setFieldError(rowData.sponsorInput, 'Sponsor is not available for this product.');
            valid = false;
          }
        });

        const initialsQuantity = this.getInitialsQuantity(rows);
        if (initialsQuantity > 0 && !this.initialsService) {
          const initialsMessage =
            this.initialsServiceError === 'multiple'
              ? 'Initials pricing is misconfigured for this collection (multiple TEAMKIT-INITIALS variants found). Please contact the store so this can be corrected.'
              : 'Initials pricing has not been configured for this collection. Please contact the store, or clear Initials on all garments to continue.';
          this.showStatus(initialsMessage, 'error');
          return false;
        }

        const numberQuantity = this.getNumberQuantity(rows);
        if (numberQuantity > 0 && !this.numberService) {
          const numberMessage =
            this.numberServiceError === 'multiple'
              ? 'Player number pricing is misconfigured for this collection (multiple TEAMKIT-NUMBER variants found). Please contact the store so this can be corrected.'
              : 'Player number pricing has not been configured for this collection. Please contact the store, or clear Player Number on all garments to continue.';
          this.showStatus(numberMessage, 'error');
          return false;
        }

        const sponsorQuantity = this.getSponsorQuantity(rows);
        if (sponsorQuantity > 0 && !this.sponsorService) {
          const configMessage =
            this.sponsorServiceError === 'multiple'
              ? 'Sponsor pricing is misconfigured for this collection (multiple TEAMKIT-SPONSOR variants found). Please contact the store so this can be corrected.'
              : 'Sponsor pricing has not been configured for this collection. Please contact the store, or uncheck Sponsor on all garments to continue.';
          this.showStatus(configMessage, 'error');
          return false;
        }

        if (sponsorQuantity > 0) {
          const sponsorNameInput = this.form.querySelector('[name="sponsor-name"]');
          if (!shared.sponsorName) {
            this.setFieldError(sponsorNameInput, 'Enter the sponsor logo name.');
            valid = false;
          }
        }

        if (!valid) {
          this.showStatus('Please fix the highlighted fields, then try again.', 'error');
          const invalid = this.querySelector('.is-invalid');
          if (invalid?.matches('[data-product-select]')) {
            invalid.closest('[data-product-picker]')?.querySelector('[data-product-picker-trigger]')?.focus();
          } else {
            invalid?.focus();
          }
        }

        return valid;
      }

      setFieldError(input, message) {
        if (!input) return;
        input.classList.add('is-invalid');
        input.setAttribute('aria-invalid', 'true');

        if (input.matches('[data-product-select]')) {
          const trigger = input.closest('[data-product-picker]')?.querySelector('[data-product-picker-trigger]');
          trigger?.classList.add('is-invalid');
          trigger?.setAttribute('aria-invalid', 'true');
        }

        const field = input.closest('[data-field]');
        const errorEl = field?.querySelector('[data-field-error]');
        if (errorEl) {
          if (!errorEl.id && input.id) {
            errorEl.id = `${input.id}-error`;
          }
          errorEl.textContent = message;
          errorEl.hidden = false;
          if (errorEl.id) {
            input.setAttribute('aria-describedby', errorEl.id);
            if (input.matches('[data-product-select]')) {
              input
                .closest('[data-product-picker]')
                ?.querySelector('[data-product-picker-trigger]')
                ?.setAttribute('aria-describedby', errorEl.id);
            }
          }
        }
      }

      clearFieldError(input) {
        if (!input) return;
        input.classList.remove('is-invalid');
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');

        if (input.matches('[data-product-select]')) {
          const trigger = input.closest('[data-product-picker]')?.querySelector('[data-product-picker-trigger]');
          trigger?.classList.remove('is-invalid');
          trigger?.removeAttribute('aria-invalid');
          trigger?.removeAttribute('aria-describedby');
        }

        const field = input.closest('[data-field]');
        const errorEl = field?.querySelector('[data-field-error]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }

      clearAllFieldErrors() {
        this.querySelectorAll('.is-invalid').forEach((el) => {
          if (el.matches('[data-product-picker-trigger]')) return;
          this.clearFieldError(el);
        });
      }

      updateTotal() {
        if (!this.totalEl) return;

        const rows = this.collectRows();
        let total = rows.reduce((sum, row) => {
          if (!row.variantId || !row.quantity || row.quantity < 1) return sum;
          return sum + row.price * row.quantity;
        }, 0);

        const sponsorQuantity = this.getSponsorQuantity(rows);
        if (sponsorQuantity > 0 && this.sponsorService) {
          total += Number(this.sponsorService.price || 0) * sponsorQuantity;
        }

        const initialsQuantity = this.getInitialsQuantity(rows);
        if (initialsQuantity > 0 && this.initialsService) {
          total += Number(this.initialsService.price || 0) * initialsQuantity;
        }

        const numberQuantity = this.getNumberQuantity(rows);
        if (numberQuantity > 0 && this.numberService) {
          total += Number(this.numberService.price || 0) * numberQuantity;
        }

        this.totalEl.textContent = this.formatMoney(total);
      }

      formatMoney(cents) {
        const amount = (Number(cents) / 100).toFixed(2);
        const format = this.moneyFormat || '{{amount}}';

        if (format.includes('{{amount_with_comma_separator}}')) {
          return format.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
        }

        if (format.includes('{{amount_no_decimals}}')) {
          return format.replace('{{amount_no_decimals}}', String(Math.round(Number(cents) / 100)));
        }

        return format.replace('{{amount}}', amount).replace('{{amount_with_apostrophe_separator}}', amount);
      }

      createOrderId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `TK-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      createRowId(index) {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `ROW-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
      }

      buildCartItems() {
        const shared = this.getSharedValues();
        const orderId = this.createOrderId();
        const rows = this.collectRows();

        const items = rows.map((rowData, index) => {
          // Visible properties: only meaningful customer-facing values (no N/A fillers).
          // Product title + variant/size already come from the Shopify line item.
          const properties = {};

          if (rowData.playerNumber && rowData.allowNumber) {
            properties['Player Number'] = rowData.playerNumber;
          }

          if (rowData.initials && rowData.allowInitials) {
            properties.Initials = rowData.initials;
          }

          if (rowData.newSponsor && rowData.allowSponsor) {
            properties['New Sponsor'] = 'Yes';
            if (shared.sponsorName) {
              properties['Sponsor Name'] = shared.sponsorName;
            }
          }

          // Private ops properties (Dawn cart templates skip keys starting with `_`).
          properties['_Team / Squad'] = shared.team;
          properties['_Manager / Contact'] = shared.manager;
          if (shared.instructions) {
            properties['_Instructions'] = shared.instructions;
          }
          properties['_Team Kit Order ID'] = orderId;
          properties['_Order Row ID'] = this.createRowId(index);
          properties['_Source'] = 'Team Kit Builder';
          properties['_Builder Version'] = this.builderVersion;

          return {
            id: Number(rowData.variantId),
            quantity: rowData.quantity,
            properties,
          };
        });

        // One sponsor service line for the whole order (qty = sum of sponsored garment qty).
        const sponsorQuantity = this.getSponsorQuantity(rows);
        if (sponsorQuantity > 0 && this.sponsorService) {
          const sponsorProperties = {
            '_Service Key': 'sponsor',
            '_Process': this.sponsorService.process || 'DTF',
            '_Position': this.sponsorService.position || '',
            '_Decoration Name': this.sponsorService.decorationName || 'Sponsor',
            '_Team Kit Order ID': orderId,
            '_Source': 'Team Kit Builder',
            '_Builder Version': this.builderVersion,
            '_Service SKU': this.sponsorService.sku || 'TEAMKIT-SPONSOR',
          };

          if (shared.sponsorName) {
            sponsorProperties['_Sponsor Logo Name'] = shared.sponsorName;
          }

          items.push({
            id: Number(this.sponsorService.variantId),
            quantity: sponsorQuantity,
            properties: sponsorProperties,
          });
        }

        // One initials service line for the whole order (qty = sum of rows with initials).
        const initialsQuantity = this.getInitialsQuantity(rows);
        if (initialsQuantity > 0 && this.initialsService) {
          items.push({
            id: Number(this.initialsService.variantId),
            quantity: initialsQuantity,
            properties: {
              '_Service Key': 'initials',
              '_Process': this.initialsService.process || 'VIN',
              '_Position': this.initialsService.position || 'Initials',
              '_Decoration Name': this.initialsService.decorationName || 'Vinyl Initials',
              '_Service SKU': this.initialsService.sku || 'TEAMKIT-INITIALS',
              '_Team Kit Order ID': orderId,
              '_Source': 'Team Kit Builder',
              '_Builder Version': this.builderVersion,
            },
          });
        }

        // One number service line for the whole order (qty = sum of rows with player numbers).
        const numberQuantity = this.getNumberQuantity(rows);
        if (numberQuantity > 0 && this.numberService) {
          items.push({
            id: Number(this.numberService.variantId),
            quantity: numberQuantity,
            properties: {
              '_Service Key': 'number',
              '_Process': this.numberService.process || 'VIN',
              '_Position': this.numberService.position || 'Back',
              '_Decoration Name': this.numberService.decorationName || 'Player Number',
              '_Service SKU': this.numberService.sku || 'TEAMKIT-NUMBER',
              '_Team Kit Order ID': orderId,
              '_Source': 'Team Kit Builder',
              '_Builder Version': this.builderVersion,
            },
          });
        }

        return { items, orderId };
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitting) return;

        this.clearStatus();
        if (!this.validate()) return;

        const { items } = this.buildCartItems();
        if (!items.length) {
          this.showStatus('Add at least one garment row.', 'error');
          return;
        }

        this.setSubmitting(true);

        try {
          // Dawn Section Rendering integration (see product-form.js)
          const payload = {
            items,
            sections_url: window.location.pathname,
          };

          if (this.cart?.getSectionsToRender) {
            payload.sections = this.cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',');
            this.cart.setActiveElement?.(document.activeElement);
          }

          const config = typeof fetchConfig === 'function' ? fetchConfig('json') : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          };

          const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            ...config,
            body: JSON.stringify(payload),
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            const message =
              data.description ||
              data.message ||
              (typeof data.errors === 'string' ? data.errors : null) ||
              'Sorry, something went wrong adding this order to the basket.';
            this.showStatus(message, 'error');

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'team-kit-order-builder',
                errors: data.errors || data.description,
                message: data.message,
              });
            }
            return;
          }

          // Multi-item responses expose items[]; notification renderContents expects .key
          if (!data.key && Array.isArray(data.items) && data.items.length) {
            data.key = data.items[data.items.length - 1].key;
          }

          if (!this.cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          // Dawn pub/sub — same event bus as product-form.js / cart.js
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'team-kit-order-builder',
              cartData: data,
            });
          }

          if (typeof this.cart.renderContents === 'function') {
            this.cart.renderContents(data);
          }

          this.showStatus('Team kit order added to your basket.', 'success');
        } catch (error) {
          console.error('[team-kit-order-builder] Cart add failed', error);
          this.showStatus('Sorry, something went wrong adding this order to the basket. Your form values have been kept.', 'error');
        } finally {
          this.setSubmitting(false);
        }
      }

      setSubmitting(isSubmitting) {
        this.submitting = isSubmitting;

        if (!this.submitButton) return;

        if (isSubmitting) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
          this.spinner?.classList.remove('hidden');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.spinner?.classList.add('hidden');
        }
      }

      showStatus(message, type) {
        if (!this.statusEl) return;
        this.statusEl.hidden = false;
        this.statusEl.textContent = message;
        this.statusEl.classList.remove(
          'team-kit-order-builder__status--error',
          'team-kit-order-builder__status--success'
        );
        this.statusEl.classList.add(
          type === 'success' ? 'team-kit-order-builder__status--success' : 'team-kit-order-builder__status--error'
        );
      }

      clearStatus() {
        if (!this.statusEl) return;
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        this.statusEl.classList.remove(
          'team-kit-order-builder__status--error',
          'team-kit-order-builder__status--success'
        );
      }
    }
  );
}
